/**
 * Client for the FastAid OS local print agent (see /print-agent).
 *
 * The production dashboard is served from the cloud, so it cannot see the
 * printers attached to the user's PC. A small agent on the workstation exposes
 * printer detection and raw ESC/POS printing on loopback. Every helper here
 * fails soft: when the agent is not installed the caller falls back to the
 * legacy server-side behaviour.
 */
import { ENV } from '../config/env';

const STORAGE_KEY = 'print_agent_url';
const TOKEN_STORAGE_KEY = 'print_agent_token';

export const DEFAULT_PRINT_AGENT_URL = 'http://127.0.0.1:9110';

/** Header the agent requires on every API call (forces a CORS preflight). */
export const PRINT_AGENT_HEADER = 'X-FirstAid-Agent';

/** Error carrying the HTTP status returned by the agent. */
export class PrintAgentError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'PrintAgentError';
    this.status = status;
  }
}

export interface AgentPrinter {
  name: string;
  shareName: string;
  portName: string;
  driverName: string;
  shared: boolean;
}

export interface AgentHealth {
  ok: boolean;
  name: string;
  version: string;
  platform: string;
  hostname: string;
  agentUrl: string;
  /** IPv4 addresses of the workstation (used to auto-fill the Network IP). */
  ipAddresses?: string[];
  /** Whether this agent may print to printers hosted on other PCs. */
  allowRemotePrint?: boolean;
  /** Remote printer targets this agent is allowed to print to. */
  allowedRemotePrinters?: string[];
}

/** Base URL of the local agent. Per-workstation override, else env, else default. */
export function getPrintAgentUrl(): string {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)?.trim();
    if (saved) return saved.replace(/\/+$/, '');
  } catch {
    /* localStorage unavailable — fall through */
  }
  return (ENV.VITE_PRINT_AGENT_URL || DEFAULT_PRINT_AGENT_URL).replace(/\/+$/, '');
}

export function setPrintAgentUrl(url: string): void {
  try {
    const value = url.trim();
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Optional shared token, kept per-workstation in localStorage (never shipped in
 * the public bundle). Only needed when the agent runs with PRINT_AGENT_TOKEN.
 */
export function getPrintAgentToken(): string {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

export function setPrintAgentToken(token: string): void {
  try {
    const value = token.trim();
    if (value) localStorage.setItem(TOKEN_STORAGE_KEY, value);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/* ── Reachability tracking ────────────────────────────────────────────────────
 * Every probe of the loopback agent is a cross-origin request. When the agent
 * is not running — or the browser blocks the request (Chrome's Local Network
 * Access gate, a missing origin, etc.) — the browser logs the failure itself and
 * we cannot catch it. Background callers probe every 30 s, so a persistent
 * failure floods the console. We therefore count consecutive network-level
 * failures, pause *passive* probes for an exponentially growing window, and
 * expose the state so the UI can show a one-time fix hint.
 *
 * User-initiated actions (printing, "Detect", "Check reachability", retry) pass
 * `force = true`, which bypasses the backoff so nothing a person waits on is
 * ever skipped. A successful response — of any status — clears the backoff. */

const UNREACHABLE_BASE_MS = 30_000;
const UNREACHABLE_MAX_MS = 5 * 60_000;

export interface PrintAgentStatus {
  /** Agent base URL last used. */
  url: string;
  /** True when the last completed request reached the agent. */
  reachable: boolean;
  /** True when recent requests failed at the network level. */
  unreachable: boolean;
  /** Consecutive network-level failures (reset on the next success). */
  failures: number;
}

let status: PrintAgentStatus = {
  url: getPrintAgentUrl(),
  reachable: false,
  unreachable: false,
  failures: 0,
};
let consecutiveFailures = 0;
let retryAt = 0;
const statusListeners = new Set<(status: PrintAgentStatus) => void>();

function emitStatus(): void {
  for (const listener of statusListeners) {
    try {
      listener(status);
    } catch {
      /* a listener must never break the probe that triggered it */
    }
  }
}

/** Current agent reachability, for UI and background scheduling. */
export function getPrintAgentStatus(): PrintAgentStatus {
  return status;
}

/**
 * Subscribe to reachability changes. The listener is invoked immediately with
 * the current status. Returns an unsubscribe function.
 */
export function subscribePrintAgentStatus(
  listener: (status: PrintAgentStatus) => void,
): () => void {
  statusListeners.add(listener);
  listener(status);
  return () => {
    statusListeners.delete(listener);
  };
}

/** True when recent failures have paused passive probes (backoff active). */
export function isPrintAgentProbeSuppressed(): boolean {
  return Date.now() < retryAt;
}

/** Internal: skip a passive probe during backoff unless the caller forces one. */
function probeSuppressed(force?: boolean): boolean {
  return !force && isPrintAgentProbeSuppressed();
}

function markAgentReachable(): void {
  if (consecutiveFailures === 0 && status.reachable && retryAt === 0) return;
  consecutiveFailures = 0;
  retryAt = 0;
  status = { url: getPrintAgentUrl(), reachable: true, unreachable: false, failures: 0 };
  emitStatus();
}

function markAgentUnreachable(): void {
  consecutiveFailures += 1;
  const delay = Math.min(
    UNREACHABLE_BASE_MS * 2 ** (consecutiveFailures - 1),
    UNREACHABLE_MAX_MS,
  );
  retryAt = Date.now() + delay;
  status = {
    url: getPrintAgentUrl(),
    reachable: false,
    unreachable: true,
    failures: consecutiveFailures,
  };
  emitStatus();
}

async function agentFetch<T>(path: string, init: RequestInit, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = getPrintAgentToken();
  try {
    const res = await fetch(`${getPrintAgentUrl()}${path}`, {
      ...init,
      headers: {
        ...(init.headers || {}),
        [PRINT_AGENT_HEADER]: '1',
        ...(token ? { 'X-Auth-Token': token } : {}),
      },
      signal: controller.signal,
      cache: 'no-store',
    });
    // A response (whatever its status) means the browser let the request
    // through to the agent, so the agent is reachable — clear any backoff.
    markAgentReachable();
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      // The agent replies with `{ "error": "..." }`. Surface that message rather
      // than the raw JSON so callers can show a readable reason (e.g. "Remote
      // printing is disabled…").
      let message = text || `Print agent responded with ${res.status}`;
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.error === 'string' && parsed.error.trim()) {
          message = parsed.error.trim();
        }
      } catch {
        /* not JSON — keep the raw text */
      }
      throw new PrintAgentError(message, res.status);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

/** Returns the agent health payload, or null when no agent is reachable. */
export async function getPrintAgentHealth(
  timeoutMs = 1200,
  force = false,
): Promise<AgentHealth | null> {
  if (probeSuppressed(force)) return null;
  try {
    const health = await agentFetch<AgentHealth>('/health', { method: 'GET' }, timeoutMs);
    return health?.ok ? health : null;
  } catch (err) {
    // A PrintAgentError means the agent answered (e.g. 401); only a genuine
    // network/CORS/timeout failure counts towards the backoff.
    if (!(err instanceof PrintAgentError)) markAgentUnreachable();
    return null;
  }
}

/**
 * Probe the local agent while distinguishing "no agent running" from "agent
 * answered with an error".
 *
 * Resolves to the health payload when the agent is up, `null` when nothing is
 * listening on the agent port (network error/timeout), and throws
 * {@link PrintAgentError} when the agent rejected the request (e.g. `401` for a
 * missing token). Callers use this to surface the real configuration problem
 * instead of silently falling back to a path that cannot work.
 *
 * Passive callers get `null` during backoff; pass `force = true` for a probe a
 * person is waiting on so it always runs.
 */
export async function probePrintAgent(
  timeoutMs = 1200,
  force = false,
): Promise<AgentHealth | null> {
  if (probeSuppressed(force)) return null;
  try {
    const health = await agentFetch<AgentHealth>('/health', { method: 'GET' }, timeoutMs);
    return health?.ok ? health : null;
  } catch (err) {
    if (err instanceof PrintAgentError) throw err;
    markAgentUnreachable();
    return null; // network/timeout — no agent listening on this machine
  }
}

/** True when a local print agent is running and responding. */
export async function isPrintAgentAvailable(
  timeoutMs = 1200,
  force = false,
): Promise<boolean> {
  if (probeSuppressed(force)) return false;
  try {
    return (await probePrintAgent(timeoutMs, force)) !== null;
  } catch {
    return false;
  }
}

/**
 * Force an immediate reachability probe, bypassing any backoff. Used by the UI
 * "retry" affordance after the user grants local-network access or starts the
 * agent. Returns the health payload, or null when still unreachable.
 */
export async function refreshPrintAgentStatus(timeoutMs = 1500): Promise<AgentHealth | null> {
  return getPrintAgentHealth(timeoutMs, true);
}

/**
 * Detect printers on the workstation via the agent.
 * Resolves to `null` when the agent is not reachable, so callers can distinguish
 * "no agent" from "agent found no printers". Throws a readable error when the
 * agent answered but rejected the call (e.g. `401` for a missing token).
 */
export async function agentDetectPrinters(
  timeoutMs = 5000,
  force = false,
): Promise<AgentPrinter[] | null> {
  if (probeSuppressed(force)) return null;
  try {
    const res = await agentFetch<{ ok: boolean; printers: AgentPrinter[] }>(
      '/printers',
      { method: 'GET' },
      timeoutMs,
    );
    return Array.isArray(res.printers) ? res.printers : [];
  } catch (err) {
    if (err instanceof PrintAgentError && err.status === 401) {
      err.message =
        'The print agent requires an access token. Configure it for this browser and retry.';
      throw err;
    }
    if (err instanceof PrintAgentError && err.status === 403) {
      err.message =
        'The print agent does not allow this dashboard origin. Add it to PRINT_AGENT_ORIGINS on the workstation.';
      throw err;
    }
    if (!(err instanceof PrintAgentError)) markAgentUnreachable();
    // Network/timeout — no agent running on this machine.
    return null;
  }
}

/** Send raw base64-encoded ESC/POS bytes to a shared printer via the agent. */
export async function agentRawPrint(
  base64: string,
  shareName?: string,
  host?: string,
  timeoutMs = 15000,
): Promise<void> {
  await agentFetch<{ ok: boolean }>(
    '/raw-print',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: base64, shareName, host }),
    },
    timeoutMs,
  );
}

/**
 * Format of a document sent to the agent's `/print-document` endpoint. `raw`
 * forwards bytes unchanged (what the spooler receives is the file's bytes),
 * `pdf`/`image` are handed to the Windows shell so the printer's normal driver
 * rasterises them — this is how a regular (A4) printer is driven, unlike the
 * ESC/POS `/raw-print` path used by thermal receipt/label printers.
 */
export type AgentDocumentFormat = 'raw' | 'pdf' | 'image';

/**
 * Print a document on the workstation's normal (non-thermal) printer through the
 * agent. Use this for regular printers whose driver cannot accept raw ESC/POS.
 */
export async function agentPrintDocument(
  base64: string,
  format: AgentDocumentFormat,
  shareName?: string,
  host?: string,
  timeoutMs = 30000,
): Promise<void> {
  await agentFetch<{ ok: boolean }>(
    '/print-document',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: base64, format, shareName, host }),
    },
    timeoutMs,
  );
}

export interface AgentShareCheck {
  ok: boolean;
  reachable: boolean;
  /** The `\\host\share` path that was checked. */
  target: string;
  /** Failure reason when `reachable` is false. */
  error?: string;
}

/**
 * Ask the local agent whether a shared printer (`\\host\share`) is reachable
 * from this PC. This runs on the workstation because neither the browser nor
 * the cloud backend can open an SMB share. The agent probes it with the same
 * spooler call the print path uses, so `reachable: true` means printing should
 * succeed.
 */
export async function agentCheckShare(
  shareName: string,
  host?: string,
  timeoutMs = 20000,
): Promise<AgentShareCheck> {
  return agentFetch<AgentShareCheck>(
    '/check-share',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shareName, host }),
    },
    timeoutMs,
  );
}

export interface AgentShareResult {
  ok: boolean;
  shared: boolean;
  printerName?: string;
  shareName?: string;
}

/**
 * Ask the local agent to enable the Windows share for one of its local printers
 * so other PCs can target it as `\\this-pc\<share>`. Throws a readable
 * {@link PrintAgentError} when the agent isn't elevated (403) or the share
 * could not be created.
 */
export async function agentSharePrinter(
  printerName: string,
  shareName: string,
  timeoutMs = 25000,
): Promise<AgentShareResult> {
  return agentFetch<AgentShareResult>(
    '/share-printer',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ printerName, shareName }),
    },
    timeoutMs,
  );
}

export interface AgentIdentity {
  hostname: string;
  ipAddresses: string[];
}

let cachedIdentity: AgentIdentity | null = null;

/**
 * Hostname + IPv4 addresses of the workstation the agent runs on, cached for
 * the session. Used to decide whether a printer host is this PC (local) or on
 * another PC (delivered via the cloud relay). Returns empty values when no agent
 * is reachable.
 *
 * Passive callers get empty values during backoff; pass `force = true` when a
 * person is waiting on the identity (e.g. adding a device).
 */
export async function getPrintAgentIdentities(force = false): Promise<AgentIdentity> {
  if (cachedIdentity) return cachedIdentity;
  if (probeSuppressed(force)) return { hostname: '', ipAddresses: [] };
  const health = await getPrintAgentHealth(1200, force);
  if (health) {
    cachedIdentity = {
      hostname: health.hostname?.trim() || '',
      ipAddresses: (health.ipAddresses || []).filter(Boolean),
    };
  }
  return cachedIdentity ?? { hostname: '', ipAddresses: [] };
}

/**
 * Hostname of the workstation the agent is running on, cached for the session.
 * Used to tag this PC's devices in the global registry so other PCs can target
 * them. Returns an empty string when no agent is reachable.
 */
export async function getPrintAgentHostname(): Promise<string> {
  return (await getPrintAgentIdentities()).hostname;
}

/** Base64-encode a Blob (e.g. a rendered PDF) for the agent's document endpoint. */
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export interface AgentRemotePrinter {
  name: string;
  /** UNC target, e.g. `\\RECEPTION-PC\Rongta80`. */
  target: string;
  /**
   * True when the printer is installed on this PC. The agent lists it for
   * visibility but it never needs (or consults) the remote allow-list.
   */
  local?: boolean;
}

/**
 * Push the printers configured in the dashboard to the local agent so the agent
 * UI can list each one with an Allow toggle. When `origins` is provided, the
 * agent also replaces its dashboard-origin allow-list. Fire-and-forget; failures
 * are ignored.
 */
export async function agentSyncPrinters(
  printers: AgentRemotePrinter[],
  origins?: string[],
  timeoutMs = 5000,
): Promise<void> {
  await agentFetch<{ ok: boolean }>(
    '/printers/sync',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(origins && origins.length > 0 ? { printers, origins } : { printers }),
    },
    timeoutMs,
  );
}

/**
 * Push only the dashboard-origin allow-list to the local agent, leaving its
 * printer list untouched.
 */
export async function agentSyncAllowedOrigins(
  origins: string[],
  timeoutMs = 5000,
): Promise<void> {
  await agentFetch<{ ok: boolean }>(
    '/printers/sync',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ origins }),
    },
    timeoutMs,
  );
}

export interface AgentHostResolution {
  ok: boolean;
  /** The host that was resolved (empty means this machine). */
  host: string;
  /** PC name (NetBIOS/DNS host name) for the host. */
  pcName: string;
  /** IPv4 address(es) for the host. */
  ipAddresses: string[];
  /** Present when resolution failed (e.g. host not found). */
  error?: string | null;
}

/**
 * Ask the local agent to resolve a PC name or IP to both its host name and
 * IPv4 address. The browser cannot do DNS, so the dashboard uses this to fill
 * the "PC name" and "Network IP" fields automatically. Returns `null` when no
 * agent is reachable (caller should fall back to what it already knows).
 */
export async function agentResolveHost(
  host: string,
  timeoutMs = 5000,
): Promise<AgentHostResolution | null> {
  try {
    return await agentFetch<AgentHostResolution>(
      '/resolve-host',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ host }),
      },
      timeoutMs,
    );
  } catch {
    return null;
  }
}
